import {
  differenceInMilliseconds,
  format,
  intervalToDuration,
  addMilliseconds,
  add,
  differenceInMinutes,
  formatDistanceStrict,
  formatDistance,
  differenceInWeeks,
  differenceInDays,
} from 'date-fns';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';

const UTC_TIMEZONE_INDICATOR = 'Z';
const NO_DATE_PROVIDED = 'N/A';

export const DATE_FORMAT = 'yyyy-MM-dd';
export const TIME_FORMAT = 'hh:mm a';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;

/**
 *
 * @param {string} date
 * @returns {string} Formatted date converted to local time zone. This is specifically for datetime “string”s that are stored as UTC but do NOT have the Z on the end
 */
export const formatUTCDateTime = (date, localTimezone) => {
  return date
    ? format(
        utcToZonedTime(`${date}${UTC_TIMEZONE_INDICATOR}`, localTimezone),
        DATE_TIME_FORMAT
      )
    : NO_DATE_PROVIDED;
};
/**
 *
 * @param {String} date
 * @returns {String} Formatted shortened date for easy reading. e.g. Wed, 22 Dec 21
 */
export const formatDateShort = (date) =>
  new Date(date).toLocaleDateString('en-AU', {
    weekday: 'short',
    year: '2-digit',
    month: 'short',
    day: 'numeric',
  });
/**
 *
 * @param {string} date
 * @returns {string} Formatted date converted to local time zone
 */
export const formatDateTimeAddMs = (date, ms) => {
  const endDate = date
    ? format(addMilliseconds(date, ms), DATE_TIME_FORMAT)
    : NO_DATE_PROVIDED;

  return endDate;
};
/**
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {number} Duration in days between startDate and endDate
 */
export const calculateDurationInDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return differenceInDays(end, start);
};

/**
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string} Duration between startDate and endDate as a formatted string
 */
export const calculateFormattedDuration = (startDate, endDate, unit) => {
  if (unit === 'week') {
    const duration = Math.abs(
      differenceInWeeks(new Date(startDate), new Date(endDate))
    );
    if (duration > 0) {
      return `${duration} weeks`;
    } else {
      return formatDistance(new Date(startDate), new Date(endDate));
    }
  } else {
    return formatDistanceStrict(new Date(startDate), new Date(endDate), {
      unit: [unit],
    });
  }
};

/**
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string} Calculates duration between startDate and endDate and returns unformatted interval
 */
export const calculateDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  return intervalToDuration({ start, end });
};

/**
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {number} Progress of today between startDate and endDate ( * 100 )
 */
export const calculateProgress = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const result = Math.trunc(
    Math.min(((Date.now() - start) / (end - start)) * 100, 100)
  );
  return Math.max(0, result);
};

/**
 *
 * @param {string} projStartDate
 * @param {string} projEndDate
 * @param {string} startDate
 * @param {string} endDate
 * @returns {string} Duration of difference in project lengths representing overtime
 */
export const calculateDifference = (
  projStartDate,
  projEndDate,
  startDate,
  endDate,
  unitOfMeasure
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const projStart = new Date(projStartDate);
  const projEnd = new Date(projEndDate);
  const diff = differenceInMilliseconds(end, start);
  const projectedDiff = differenceInMilliseconds(projEnd, projStart);

  return calculateFormattedDuration(
    start,
    new Date(
      addMilliseconds(
        start,
        Math.max(diff, projectedDiff) - Math.min(diff, projectedDiff)
      )
    ),
    unitOfMeasure
  );
};
/**
 *
 * @param {string} projStartDate
 * @param {string} projEndDate
 * @param {string} startDate
 * @param {string} endDate
 * @returns {boolean} True if late, false if early
 */
export const calculateLate = (
  projStartDate,
  projEndDate,
  startDate,
  endDate
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const projStart = new Date(projStartDate);
  const projEnd = new Date(projEndDate);

  return (
    differenceInMinutes(start, end) < differenceInMinutes(projStart, projEnd)
  );
};
/**
 * @param {Date} startDate
 * @param {integer} measurement
 * @param {string} unitOfMeasure
 * @returns {Date} Date of the timelineObjective
 */
export const constructObjectiveDate = (startDate, measurement, unitOfMeasure) =>
  add(startDate, { [unitOfMeasure]: measurement });
